import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import { useMaxWidthBetweenSideToolbars } from "@/components/common/ui-overlay-provider";
import { isAddWallState } from "@/components/ui/pano-measurement-tool-panel";
import { selectHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { selectIsLocationMarkerCreationEnabled } from "@/store/location-marker-slice";
import {
  selectActiveMeasurement,
  selectPanoMeasurementToolState,
} from "@/store/measurement-tool-selector";
import { PanoMeasurementToolState } from "@/store/measurement-tool-slice";
import { selectModeName } from "@/store/mode-selectors";
import { selectNumberOfSessionAnalyses } from "@/store/point-cloud-analysis-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { useMeasurementGuidanceStore } from "@/tools/multi-point-measures/measurement-guidance-store";
import { ContextualBanner, FaroText, neutral } from "@faro-lotv/flat-ui";
import { isMacOS } from "@faro-lotv/foundation";
import { Box, SxProps, Theme, useMediaQuery } from "@mui/material";
import { PropsWithChildren, ReactNode, type JSX } from "react";
import { AvailableAnnotationTools } from "../../r3f/renderers/annotations/annotations-types";

type Props = {
  /** Additional styling props */
  sx?: SxProps<Theme>;
};

type GeneralHelpBannerProps = Props & {
  /** Text to show in the banner */
  text: ReactNode;

  /** Name of the tool to check. When the tool is activated, the banner will be shown */
  toolToCheck: ToolName;

  /** If true, then the banner will be hidden. @default false */
  hidden?: boolean;
};

function GeneralHelpBanner({
  text,
  toolToCheck,
  hidden,
  sx,
}: GeneralHelpBannerProps): JSX.Element | null {
  const activeTool = useAppSelector(selectActiveTool);

  if (activeTool !== toolToCheck || hidden) return null;
  return <ContextualBanner sx={sx}>{text}</ContextualBanner>;
}

/** @returns a keyboard key rendered as a key cap */
function Key({ children }: PropsWithChildren): JSX.Element {
  return (
    <Box
      component="kbd"
      sx={{
        px: 0.75,
        mx: 0.25,
        border: `1px solid ${neutral[500]}`,
        borderRadius: "4px",
        fontFamily: "inherit",
        fontSize: "0.85em",
        fontWeight: "bold",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * @returns a one-line hint about the current measurement step, replaced by a confirmation while
 * SHIFT or CMD/CTRL is held. On touch devices the commands are in the MeasurementTouchToolbar.
 */
export function MeasurementHelpBanner({ sx }: Props): JSX.Element | null {
  const activeMeasure = useAppSelector(selectActiveMeasurement);
  const panoMeasureState = useAppSelector(selectPanoMeasurementToolState);
  const isSheet = useAppSelector(selectModeName) === "sheet";
  const isTouch = useMediaQuery("(pointer: coarse)");
  const { edgeCornerDetection } = useFeatureDetectionContext();
  const { livePointsCount, isAxisLocked } = useMeasurementGuidanceStore();
  const maxWidth = useMaxWidthBetweenSideToolbars();

  // Snapping runs only on point clouds and panoramas with depth (walk and overview modes)
  const canSnap =
    !isTouch && !isSheet && panoMeasureState === PanoMeasurementToolState.None;

  let hint: ReactNode;
  if (isAxisLocked && livePointsCount > 0) {
    hint = isSheet
      ? "Locked to the X or Y axis"
      : "Locked to horizontal or vertical";
  } else if (edgeCornerDetection && canSnap) {
    hint = "Snapping to edges and corners";
  } else {
    hint = getStepHint(livePointsCount, isTouch, canSnap);
  }

  return (
    <GeneralHelpBanner
      text={
        <FaroText variant="bodyL" dark>
          {hint}
        </FaroText>
      }
      toolToCheck={ToolName.measurement}
      hidden={!!activeMeasure || isAddWallState(panoMeasureState)}
      sx={{ ...sx, maxWidth }}
    />
  );
}

/**
 * @param pointsCount number of points placed in the measurement being taken
 * @param isTouch true on touch devices, where keyboard shortcuts are not available
 * @param canSnap true if the snapping to edges and corners is available
 * @returns the hint for the next action of the measurement being taken
 */
function getStepHint(
  pointsCount: number,
  isTouch: boolean,
  canSnap: boolean,
): ReactNode {
  const action = isTouch ? "Tap" : "Click";
  if (pointsCount === 0) return `${action} to place the first point`;
  if (isTouch) {
    return pointsCount === 1
      ? "Tap to add more points"
      : "Tap the first point to close the area, or tap Done";
  }
  if (pointsCount === 1) {
    return (
      <>
        Click to add more points · Hold <Key>SHIFT</Key> to lock the direction
        {canSnap && (
          <>
            {" "}
            · <Key>{isMacOS() ? "CMD" : "CTRL"}</Key> to snap to edges
          </>
        )}
      </>
    );
  }
  return (
    <>
      <Key>ESC</Key> to finish · Click the first point to close the area ·{" "}
      <Key>BACKSPACE</Key> to undo
    </>
  );
}

interface CreateAnnotationHelpBannerProps extends Props {
  /** Available tool/s to which the help text needs to be updated */
  availableTool: AvailableAnnotationTools;
}

/**
 * @returns a help message banner which can be used for informing how to create a new Annotation
 */
export function CreateAnnotationHelpBanner({
  availableTool,
  sx,
}: CreateAnnotationHelpBannerProps): JSX.Element | null {
  const hasCreatedNewAnnotation = useAppSelector(selectHasCreatedNewAnnotation);

  let helpText = "";
  switch (availableTool) {
    case AvailableAnnotationTools.rectangle:
      helpText = "Click and drag to draw the annotation rectangle.";
      break;
    case AvailableAnnotationTools.singlePoint:
      helpText =
        "Click the point at which you want to insert a point annotation.";
      break;
    case AvailableAnnotationTools.all:
      helpText =
        "Click to insert a point annotation or click and drag to insert an area annotation.";
      break;
  }
  return (
    <GeneralHelpBanner
      text={helpText}
      toolToCheck={ToolName.annotation}
      hidden={hasCreatedNewAnnotation}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner which can be used for informing how the analysis can be performed
 */
export function AnalysisHelpBanner({ sx }: Props): JSX.Element | null {
  const isFirstAnalysis = useAppSelector(selectNumberOfSessionAnalyses) === 0;

  return (
    <GeneralHelpBanner
      text="Close the polygon by clicking the first point."
      toolToCheck={ToolName.analysis}
      hidden={!isFirstAnalysis}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner for the edit waypoints tool
 */
export function EditWaypointsHelpBanner({ sx }: Props): JSX.Element {
  const isLocationMarkerCreationEnabled = useAppSelector(
    selectIsLocationMarkerCreationEnabled,
  );

  return (
    <GeneralHelpBanner
      text={
        isLocationMarkerCreationEnabled
          ? "Click to add location markers"
          : "Drag waypoints to reposition them"
      }
      toolToCheck={ToolName.editWaypoints}
      sx={sx}
    />
  );
}
